/**
 * networkRetryMixin (GID-2575)
 *
 * Wraps a data fetch in exponential-backoff auto-retry plus a reactive,
 * per-key state machine, so a flaky field connection no longer leaves a view
 * silently empty. Built for the partner at-work order view: the protocol form,
 * form answers and briefing documents were each loaded by a fetch whose
 * `.catch` was silent (or absent), so a timeout produced a blank section with
 * no error shown and no trace anywhere.
 *
 * Usage:
 *   mixins: [networkRetryMixin],
 *   methods: {
 *     fetchThing() {
 *       return this.runWithRetry('thing', () =>
 *         axios.get(url).then(({ data }) => { this.thing = data; }));
 *     },
 *   },
 * Template:
 *   v-if="netRetry.thing && netRetry.thing.hadError"   → connectivity message
 *   @click="retryNow('thing')"                          → manual retry button
 *
 * Only *network-class* errors (timeout / no response / 5xx) trigger retries and
 * the error state. A clean 4xx (e.g. 404 "no form for this job") is a
 * definitive non-network result: status 'failed', logged, and NOT surfaced as a
 * connectivity problem — preserving the previous behaviour for those.
 */

import * as Sentry from '@sentry/browser';

const DEFAULTS = {
  // ~1 + 2 + 4 + 8 + 16 + 30s of auto-retry before falling back to manual retry.
  maxAttempts: 6,
  baseDelay: 1000,
  maxDelay: 30000,
};

export default {
  data() {
    return {
      // Keyed reactive state: { [key]: { status, attempt, retrying, hadError } }
      // status: 'loading' | 'loaded' | 'error' (network) | 'failed' (non-network)
      // hadError stays true after the first network failure until a load
      // succeeds, so the message survives the loading flicker of a retry.
      netRetry: {},
    };
  },
  methods: {
    isNetworkError(error) {
      if (!error) return false;
      if (error.code === 'ECONNABORTED') return true; // axios timeout
      if (error.response) return error.response.status >= 500; // 5xx retry; 4xx no
      if (error.request) return true; // request sent, no response → offline / network
      if (error.message === 'Network Error') return true;
      return false; // programming error etc. — never retry
    },
    runWithRetry(key, thunk, opts = {}) {
      this._netRetryThunks = this._netRetryThunks || {};
      this._netRetryTimers = this._netRetryTimers || {};
      this._netRetryCfg = this._netRetryCfg || {};
      this._netRetryThunks[key] = thunk;
      this._netRetryCfg[key] = { ...DEFAULTS, ...opts };
      return this._netRetryAttempt(key, 1);
    },
    retryNow(key) {
      if (this._netRetryTimers && this._netRetryTimers[key]) {
        clearTimeout(this._netRetryTimers[key]);
        this._netRetryTimers[key] = null;
      }
      if (!this._netRetryThunks || !this._netRetryThunks[key]) return undefined;
      return this._netRetryAttempt(key, 1);
    },
    async _netRetryAttempt(key, attempt) {
      const cfg = this._netRetryCfg[key];
      const prev = this.netRetry[key] || {};
      this.$set(this.netRetry, key, {
        status: 'loading',
        attempt,
        retrying: attempt > 1 || !!prev.hadError,
        hadError: !!prev.hadError,
      });
      try {
        await this._netRetryThunks[key]();
        this.$set(this.netRetry, key, {
          status: 'loaded',
          attempt,
          retrying: false,
          hadError: false,
        });
      } catch (error) {
        if (this.isNetworkError(error)) {
          const hasMore = attempt < cfg.maxAttempts;
          this.$set(this.netRetry, key, {
            status: 'error',
            attempt,
            retrying: hasMore, // an auto-retry is scheduled
            hadError: true,
          });
          if (hasMore) {
            const backoff = Math.min(
              cfg.maxDelay,
              cfg.baseDelay * 2 ** (attempt - 1),
            );
            const jitter = Math.floor(Math.random() * cfg.baseDelay);
            this._netRetryTimers[key] = setTimeout(
              () => this._netRetryAttempt(key, attempt + 1),
              backoff + jitter,
            );
          } else {
            // Auto-retries exhausted — the partner is genuinely stuck on a bad
            // connection. Record it so these real field-connectivity failures are
            // measurable instead of invisible (the original GID-2575 blind spot).
            this._captureNetRetryError(key, error, 'exhausted');
          }
        } else {
          // Non-network (4xx / programming error): keep the old quiet behaviour
          // for the user, but capture it so it's no longer fully silent.
          this.$set(this.netRetry, key, {
            status: 'failed',
            attempt,
            retrying: false,
            hadError: false,
          });
          this._captureNetRetryError(key, error, 'non-network');
        }
      }
    },
    _captureNetRetryError(key, error, reason) {
      // eslint-disable-next-line no-console
      console.error(`[networkRetry] ${key} ${reason}`, error);
      try {
        Sentry.withScope((scope) => {
          scope.setTag('netRetryKey', key);
          scope.setTag('netRetryReason', reason);
          Sentry.captureException(error);
        });
      } catch (e) {
        // Sentry not initialised (e.g. local dev) — the console line is enough.
      }
    },
  },
  beforeDestroy() {
    if (this._netRetryTimers) {
      Object.values(this._netRetryTimers).forEach((t) => t && clearTimeout(t));
    }
  },
};
